import { CaretDownIcon } from '@phosphor-icons/react';

import { cn } from '@/shared/utils/cn';

export const ICON_SIZE = 18;
export const HEADER_CONTAINER_CLASS = 'adipa-shell';

const ACTION_BASE_INTERACTION_CLASS =
  'transition duration-adipa-fast ease-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-adipa-primary';

export const PRIMARY_ACTION_MOTION_CLASS = cn(
  ACTION_BASE_INTERACTION_CLASS,
  'shadow-adipa-card hover:bg-adipa-primary-hover hover:shadow-adipa-console-panel active:bg-adipa-primary-hover',
);

export const SECONDARY_ACTION_MOTION_CLASS = cn(
  ACTION_BASE_INTERACTION_CLASS,
  'hover:border-adipa-border-strong hover:bg-adipa-surface-lavender hover:text-adipa-primary active:bg-adipa-surface-lavender',
);

export const MENU_SURFACE_CLASS =
  'rounded-adipa-lg border border-adipa-border bg-adipa-surface p-2 shadow-adipa-console-panel';

export const MENU_ROW_CLASS = cn(
  'w-full rounded-adipa-md px-3 py-2 text-left text-sm font-medium leading-5 text-adipa-text-primary',
  'transition-colors duration-adipa-fast ease-out motion-reduce:transition-none',
  'hover:bg-adipa-surface-lavender hover:font-semibold hover:text-adipa-primary',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-adipa-primary',
);

export const MENU_ACTIVE_ROW_CLASS = 'font-semibold text-adipa-primary';

export const MENU_SECTION_LABEL_CLASS =
  'px-3 text-xs font-semibold uppercase leading-4 tracking-widest text-adipa-text-secondary';

export const MOBILE_MENU_ROW_CLASS = cn(MENU_ROW_CLASS, 'flex min-h-11 items-center');

export const MOBILE_NESTED_MENU_ROW_CLASS = cn(
  'flex min-h-11 w-full items-center rounded-adipa-md px-2.5 py-2 text-left text-sm font-medium leading-5 text-adipa-text-primary',
  'transition-colors duration-adipa-fast ease-out motion-reduce:transition-none',
  'hover:bg-adipa-surface-lavender hover:font-semibold hover:text-adipa-primary',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-adipa-primary',
);

export const getCartAriaLabel = (itemCount: number): string =>
  itemCount === 1
    ? 'Carrito de compras, 1 curso agregado'
    : `Carrito de compras, ${itemCount} cursos agregados`;

export const getFavoritesAriaLabel = (count: number): string => {
  if (count === 0) return 'Mis favoritos';
  return count === 1
    ? 'Mis favoritos, 1 programa guardado'
    : `Mis favoritos, ${count} programas guardados`;
};

export const Badge = ({
  className,
  color,
  label,
}: {
  className?: string;
  color: 'green' | 'magenta' | 'brand';
  label: string;
}) => (
  <span
    className={cn(
      'rounded-adipa-full inline-flex items-center border px-2 py-0.5 text-xs font-semibold leading-none tracking-widest',
      color === 'green' &&
        'border-adipa-success-border bg-adipa-success-surface text-adipa-success',
      color === 'magenta' && 'border-adipa-accent-border bg-adipa-accent-surface text-adipa-accent',
      color === 'brand' &&
        'border-adipa-border-strong bg-adipa-surface-lavender text-adipa-primary',
      className,
    )}
  >
    {label}
  </span>
);

export const getBadgeDotClassName = (color?: 'green' | 'magenta' | 'brand') => {
  if (color === 'green') return cn('bg-adipa-success');
  if (color === 'brand') return cn('bg-adipa-primary');
  return cn('bg-adipa-accent');
};

export const BadgeDot = ({ color }: { color: 'green' | 'magenta' | 'brand' }) => (
  <span
    aria-hidden="true"
    className={cn('rounded-adipa-full size-1.5 shrink-0', getBadgeDotClassName(color))}
  />
);

export const NavLabel =({ isActive, label }: { isActive: boolean; label: string }) => (
  <span className="relative inline-grid whitespace-nowrap">
    <span
      aria-hidden="true"
      className="invisible col-start-1 row-start-1 h-0 overflow-hidden font-semibold"
    >
      {label}
    </span>
    <span
      className={cn(
        'duration-adipa-fast col-start-1 row-start-1 font-medium transition-colors ease-out motion-reduce:transition-none',
        isActive && 'text-adipa-primary',
      )}
    >
      {label}
    </span>
  </span>
);

export const getNavTriggerClassName = (isActive: boolean, isOpen: boolean, highlight = false) =>
  cn(
    'group relative inline-flex h-9 items-center gap-1 rounded-adipa-md px-2 text-xs leading-none xl:h-10 xl:gap-1.5 xl:px-3.5 xl:text-sm',
    highlight ? 'text-adipa-text-primary' : 'text-adipa-text-secondary',
    'transition-colors duration-adipa-fast ease-out motion-reduce:transition-none',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-adipa-primary',
    'after:pointer-events-none after:absolute after:bottom-1 after:left-2 after:right-2 after:h-0.5 after:origin-center after:rounded-adipa-full after:bg-adipa-primary xl:after:left-3.5 xl:after:right-3.5',
    'after:transition-transform after:duration-adipa-fast after:ease-out motion-reduce:after:transition-none',
    isActive || isOpen
      ? 'text-adipa-primary after:scale-x-100 hover:brightness-90'
      : 'after:scale-x-0 hover:text-adipa-text-primary hover:after:scale-x-100',
  );

export const NavigationChevron = ({ isOpen }: { isOpen: boolean }) => (
  <CaretDownIcon
    className={cn('duration-adipa-fast transition-transform ease-out', isOpen && 'rotate-180')}
    size={14}
    weight="bold"
  />
);
